import { useEffect, useMemo, useState } from 'react';
import type { Track } from '@karozik/engine';
import { api, cn, Spinner, TrackLine, useTrackSearch } from '@karozik/ui';
import type { AdminEvent } from '../types';

type PoolTrack = Track & { curated: boolean };

const ENERGY = ['', '😌', '🙂', '😀', '🤩', '🔥'];

/** The autopilot's crate: the curated seed pool plus anything the organiser adds. */
export function PoolTab({ event, onChange }: { event: AdminEvent; onChange(): void }) {
  const [pool, setPool] = useState<PoolTrack[] | null>(null);
  const [filter, setFilter] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const [adding, setAdding] = useState('');
  const { result, loading } = useTrackSearch(adding, {});

  const load = () => void api<PoolTrack[]>(`/api/admin/events/${event.id}/pool`).then(setPool);
  useEffect(load, [event.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const tags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of pool ?? []) for (const g of t.tags ?? []) counts.set(g, (counts.get(g) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [pool]);

  const shown = (pool ?? []).filter(
    (t) => (!tag || t.tags?.includes(tag)) && `${t.title} ${t.artist}`.toLowerCase().includes(filter.toLowerCase()),
  );

  const add = async (id: string) => {
    await api(`/api/admin/events/${event.id}/pool`, { method: 'POST', json: { trackIds: [id] } });
    load();
    onChange();
  };
  const remove = async (id: string) => {
    await api(`/api/admin/events/${event.id}/pool/${id}`, { method: 'DELETE' });
    setPool((p) => p?.filter((t) => t.id !== id) ?? null);
    onChange();
  };
  const setEnergy = async (t: PoolTrack, energy: number) => {
    await api(`/api/admin/tracks/${t.id}`, { method: 'PATCH', json: { energy } });
    setPool((p) => p?.map((x) => (x.id === t.id ? { ...x, energy } : x)) ?? null);
  };

  if (!pool) return <Spinner />;
  const inPool = new Set(pool.map((t) => t.id));

  return (
    <div className="space-y-5">
      <p className="text-sm text-ink-dim">
        L’autopilote pioche dans ces <b>{pool.length}</b> titres selon la phase de la soirée (énergie 1 à 5). Les tags <code>slow</code>,{' '}
        <code>collective</code>, <code>birthday</code> et <code>lastdance</code> sont réservés aux moments.
      </p>

      <section className="rounded-3xl bg-surface p-4">
        <h2 className="font-semibold">Ajouter au pool</h2>
        <input value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="Chercher dans le catalogue…" className="input mt-2" />
        {loading && <Spinner className="mt-3" />}
        <ul className="mt-2 max-h-80 space-y-1.5 overflow-y-auto">
          {result?.tracks.map((t) => (
            <li key={t.id}>
              <TrackLine
                track={t}
                artSize="h-10 w-10"
                right={
                  inPool.has(t.id) ? (
                    <span className="text-sm text-lime">✓</span>
                  ) : (
                    <button onClick={() => void add(t.id)} className="rounded-lg bg-surface-2 px-3 py-1.5 text-sm">
                      Ajouter
                    </button>
                  )
                }
              />
            </li>
          ))}
        </ul>
      </section>

      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setTag(null)} className={cn('rounded-full px-3 py-1 text-xs', !tag ? 'bg-pink' : 'bg-surface-2')}>
          Tous
        </button>
        {tags.map(([g, n]) => (
          <button key={g} onClick={() => setTag(g === tag ? null : g)} className={cn('rounded-full px-3 py-1 text-xs', tag === g ? 'bg-pink' : 'bg-surface-2')}>
            {g} <span className="opacity-60">{n}</span>
          </button>
        ))}
      </div>
      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer le pool…" className="input" />

      <ul className="space-y-1.5">
        {shown.slice(0, 300).map((t) => (
          <li key={t.id} className="rounded-2xl bg-surface p-2">
            <TrackLine
              track={{ ...t, album: t.album ?? null, year: t.year ?? null, artworkUrl: t.artworkUrl ?? null }}
              artSize="h-10 w-10"
              sub={<div className="truncate text-xs text-ink-faint">{(t.tags ?? []).join(' · ')}</div>}
              right={
                <div className="flex shrink-0 items-center gap-1">
                  <select
                    value={t.energy ?? 3}
                    onChange={(e) => void setEnergy(t, Number(e.target.value))}
                    className="rounded-lg bg-surface-2 px-1.5 py-1 text-sm"
                    title="Énergie"
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>
                        {ENERGY[n]} {n}
                      </option>
                    ))}
                  </select>
                  <button onClick={() => void remove(t.id)} className="rounded-lg bg-surface-2 px-2 py-1 text-sm" title="Retirer du pool">
                    ✕
                  </button>
                </div>
              }
            />
          </li>
        ))}
      </ul>
      {shown.length > 300 && <p className="text-center text-sm text-ink-faint">… et {shown.length - 300} autres (affine le filtre)</p>}

      <button
        onClick={async () => {
          if (!confirm('Remettre le pool d’origine (sélection KaroZik) ? Tes ajouts seront retirés.')) return;
          await api(`/api/admin/events/${event.id}/pool/reset`, { method: 'POST' });
          load();
          onChange();
        }}
        className="w-full rounded-xl bg-surface-2 py-2 text-sm text-ink-dim"
      >
        Réinitialiser le pool
      </button>
    </div>
  );
}
